/**
 * User API client
 * Uses pro-api (BFF) for user operations
 * Note: Auth operations (login, register, refresh) still use user-service directly
 */

import { syncNavigationCache } from '../auth/permissions';
import {
  normalizeNavigationPayload,
  type NavigationPayload,
} from './navigation';
import {
  authenticatedProApiFetch,
  authenticatedProApiFetchWithRetry,
  proApiFetch,
} from './pro-api.client';
import { parseDisabledBySnapshot } from '../disabled-status';

export interface UserOrganization {
  id: string;
  name: string;
  logoUrl?: string | null;
  description?: string | null;
  city?: string | null;
  state?: string | null;
  isPrimary?: boolean;
  roleId?: string | null;
  roleName?: string;
  roleLabel?: string | null;
  roleColor?: string | null;
  roleSystemKey?: "admin" | "member" | string | null;
}

/** Pending email invitation to join an organization (admin user profile). */
export interface UserPendingOrganizationInvite {
  inviteId: string;
  email: string;
  organizationId: string;
  organizationName: string;
  logoUrl?: string | null;
  description?: string | null;
  city?: string | null;
  state?: string | null;
  roleId: string;
  roleName?: string | null;
  roleColor?: string | null;
  expiresAt: string;
  createdAt?: string;
}

export interface UserGlobalRole {
  id: string;
  name: string;
  color?: string | null;
}

export interface User {
  id: string;
  email: string;
  /** Original org-invite email when account email differs */
  invitedAs?: string | null;
  firstName: string;
  lastName: string;
  avatarUrl?: string | null;
  googleId?: string | null;
  googleAvatarUrl?: string | null;
  phone?: string;
  countryCode?: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  /** ISO timestamp when current email verification code expires (unverified users only). */
  emailVerificationExpiresAt?: string;
  /** ISO timestamp when current phone verification code expires (unverified phone only). */
  phoneVerificationExpiresAt?: string;
  profileSetupCompleted: boolean;
  /** UI language preference; null/undefined = unset */
  preferredLanguage?: string | null;
  /** IANA timezone preference; null/undefined = unset */
  preferredTimezone?: string | null;
  organizations: UserOrganization[];
  pendingOrganizations: UserOrganization[];
  pendingOrganizationInvites: UserPendingOrganizationInvite[];
  hasOrganizationMembership: boolean;
  hasPendingOrganizationMembership: boolean;
  /** True when the user has at least one product access grant (or is super-admin). */
  hasProductAccess: boolean;
  roles: string[];
  /** Global roles with display metadata (admin profile) */
  globalRoles?: UserGlobalRole[];
  permissions: string[];
  navigation: NavigationPayload;
  createdAt: string;
  updatedAt: string;
  /** Set when the account has been disabled (soft-deleted). */
  deletedAt?: string | null;
  disabledByUserId?: string | null;
  disabledByName?: string | null;
  disabledByEmail?: string | null;
  /** Cheap, pre-computed Smart Link sync-status badge signal — null when not linked (or link disabled). */
  smartLinkSyncStatus?: "in_sync" | "out_of_sync" | "unknown" | null;
}

function parseOrgSummary(
  org: Record<string, unknown>,
  extras?: Partial<UserOrganization>,
): UserOrganization | null {
  const id = org.id != null ? String(org.id) : "";
  const name = org.name != null ? String(org.name) : "";
  if (!id && !name) return null;

  const logoUrl =
    org.logoUrl != null && String(org.logoUrl).trim()
      ? String(org.logoUrl)
      : org.logo_url != null && String(org.logo_url).trim()
        ? String(org.logo_url)
        : undefined;
  const description = org.description != null ? String(org.description) : undefined;
  const city = org.city != null ? String(org.city) : undefined;
  const state = org.state != null ? String(org.state) : undefined;

  return {
    id: id || name,
    name: name || "Organization",
    ...(logoUrl ? { logoUrl } : {}),
    ...(description ? { description } : {}),
    ...(city ? { city } : {}),
    ...(state ? { state } : {}),
    ...extras,
  };
}

function parseOrganizations(raw: Record<string, unknown>): UserOrganization[] {
  const rows = raw.organizations;
  if (!Array.isArray(rows)) return [];

  const result: UserOrganization[] = [];
  for (const row of rows) {
    const entry = row as Record<string, unknown>;
    const org = (entry.organization ?? entry) as Record<string, unknown>;
    const role = entry.role as Record<string, unknown> | undefined;
    const roleName = entry.roleName != null
      ? String(entry.roleName)
      : role?.name != null ? String(role.name) : undefined;
    const roleColor = entry.roleColor != null
      ? String(entry.roleColor)
      : role?.color != null ? String(role.color) : undefined;
    const roleLabel = entry.roleLabel != null
      ? String(entry.roleLabel)
      : role?.label != null ? String(role.label) : undefined;
    const roleSystemKey = entry.roleSystemKey != null
      ? String(entry.roleSystemKey)
      : entry.role_system_key != null
        ? String(entry.role_system_key)
        : role?.systemKey != null
          ? String(role.systemKey)
          : role?.system_key != null
            ? String(role.system_key)
            : undefined;
    const roleId =
      entry.roleId != null
        ? String(entry.roleId)
        : entry.role_id != null
          ? String(entry.role_id)
          : role?.id != null
            ? String(role.id)
            : undefined;

    const parsed = parseOrgSummary(org, {
      ...(entry.isPrimary != null || entry.is_primary != null
        ? { isPrimary: Boolean(entry.isPrimary ?? entry.is_primary) }
        : {}),
      ...(roleId ? { roleId } : {}),
      ...(roleName ? { roleName } : {}),
      ...(roleLabel ? { roleLabel } : {}),
      ...(roleColor ? { roleColor } : {}),
      ...(roleSystemKey ? { roleSystemKey } : {}),
    });
    if (parsed) result.push(parsed);
  }
  return result;
}

function parsePendingOrganizationInvites(
  raw: Record<string, unknown>,
): UserPendingOrganizationInvite[] {
  const rows =
    raw.pendingOrganizationInvites ?? raw.pending_organization_invites;
  if (!Array.isArray(rows)) return [];

  return rows.map((row) => {
    const entry = row as Record<string, unknown>;
    const org = (entry.organization ?? entry) as Record<string, unknown>;
    return {
      inviteId: String(entry.inviteId ?? entry.invite_id ?? entry.id ?? ""),
      email: String(entry.email ?? ""),
      organizationId: String(
        entry.organizationId ??
          entry.organization_id ??
          org.id ??
          "",
      ),
      organizationName: String(
        entry.organizationName ??
          entry.organization_name ??
          org.name ??
          "Organization",
      ),
      logoUrl:
        entry.logoUrl != null
          ? String(entry.logoUrl)
          : entry.logo_url != null
            ? String(entry.logo_url)
            : org.logoUrl != null
              ? String(org.logoUrl)
              : org.logo_url != null
                ? String(org.logo_url)
                : null,
      description:
        entry.description != null
          ? String(entry.description)
          : org.description != null
            ? String(org.description)
            : null,
      city:
        entry.city != null
          ? String(entry.city)
          : org.city != null
            ? String(org.city)
            : null,
      state:
        entry.state != null
          ? String(entry.state)
          : org.state != null
            ? String(org.state)
            : null,
      roleId: String(entry.roleId ?? entry.role_id ?? ""),
      roleName:
        entry.roleName != null
          ? String(entry.roleName)
          : entry.role_name != null
            ? String(entry.role_name)
            : null,
      roleColor:
        entry.roleColor != null
          ? String(entry.roleColor)
          : entry.role_color != null
            ? String(entry.role_color)
            : null,
      expiresAt: String(entry.expiresAt ?? entry.expires_at ?? ""),
      createdAt:
        entry.createdAt != null
          ? String(entry.createdAt)
          : entry.created_at != null
            ? String(entry.created_at)
            : undefined,
    };
  });
}

function parsePendingOrganizations(raw: Record<string, unknown>): UserOrganization[] {
  const rows = raw.pendingMembershipRequests ?? raw.pending_membership_requests;
  if (!Array.isArray(rows)) return [];

  const result: UserOrganization[] = [];
  for (const row of rows) {
    const entry = row as Record<string, unknown>;
    const org = (entry.organization ?? entry) as Record<string, unknown>;
    const parsed = parseOrgSummary(org);
    if (parsed) result.push(parsed);
  }
  return result;
}

function parseGlobalRoles(
  raw: Record<string, unknown>,
): UserGlobalRole[] {
  const rolesRaw = raw.roles;
  if (!Array.isArray(rolesRaw)) return [];

  const result: UserGlobalRole[] = [];
  for (const entry of rolesRaw) {
    if (typeof entry === "string") {
      if (entry.trim()) result.push({ id: entry, name: entry });
      continue;
    }
    const row = entry as Record<string, unknown>;
    const role = (row.role ?? row) as Record<string, unknown>;
    const id = role.id != null ? String(role.id) : "";
    const name =
      role.name != null
        ? String(role.name)
        : row.name != null
          ? String(row.name)
          : "";
    if (!name) continue;
    result.push({
      id: id || name,
      name,
      color:
        role.color != null
          ? String(role.color)
          : row.color != null
            ? String(row.color)
            : null,
    });
  }
  return result;
}

/** Normalize user-service /users/me shape to pro-web User. */
export function normalizeUserProfile(raw: Record<string, unknown>): User {
  const globalRoles = parseGlobalRoles(raw);
  const roleNames = globalRoles.map((r) => r.name);

  const perms = (raw.effectivePermissions as Array<{ name?: string }> | string[] | undefined) ?? [];
  const permissionNames = Array.isArray(perms)
    ? perms.map((p) => (typeof p === "string" ? p : p.name ?? "")).filter(Boolean)
    : [];

  const organizations = parseOrganizations(raw);
  const pendingOrganizations = parsePendingOrganizations(raw);
  const pendingOrganizationInvites = parsePendingOrganizationInvites(raw);

  const navigation = normalizeNavigationPayload(raw.navigation);

  const user: User = {
    id: String(raw.id ?? ""),
    email: String(raw.email ?? ""),
    invitedAs:
      raw.invitedAs != null
        ? String(raw.invitedAs)
        : raw.invited_as != null
          ? String(raw.invited_as)
          : null,
    firstName: String(raw.firstName ?? ""),
    lastName: String(raw.lastName ?? ""),
    avatarUrl:
      raw.avatarUrl != null
        ? String(raw.avatarUrl)
        : raw.avatar_url != null
          ? String(raw.avatar_url)
          : null,
    googleId:
      raw.googleId != null
        ? String(raw.googleId)
        : raw.google_id != null
          ? String(raw.google_id)
          : null,
    googleAvatarUrl:
      raw.googleAvatarUrl != null
        ? String(raw.googleAvatarUrl)
        : raw.google_avatar_url != null
          ? String(raw.google_avatar_url)
          : null,
    phone: raw.phone != null ? String(raw.phone) : undefined,
    countryCode: raw.countryCode != null ? String(raw.countryCode) : undefined,
    emailVerified: Boolean(raw.emailVerified ?? raw.isEmailVerified),
    phoneVerified: Boolean(raw.phoneVerified ?? raw.isPhoneVerified),
    ...(raw.emailVerificationCodeExpiresAt != null ||
    raw.email_verification_code_expires_at != null
      ? {
          emailVerificationExpiresAt: String(
            raw.emailVerificationCodeExpiresAt ?? raw.email_verification_code_expires_at,
          ),
        }
      : {}),
    ...(raw.phoneVerificationCodeExpiresAt != null ||
    raw.phone_verification_code_expires_at != null
      ? {
          phoneVerificationExpiresAt: String(
            raw.phoneVerificationCodeExpiresAt ?? raw.phone_verification_code_expires_at,
          ),
        }
      : {}),
    profileSetupCompleted: Boolean(
      raw.profileSetupCompleted ??
        raw.profile_setup_completed ??
        (raw.profileSetupCompletedAt != null && String(raw.profileSetupCompletedAt) !== ""),
    ),
    preferredLanguage:
      raw.preferredLanguage != null
        ? String(raw.preferredLanguage)
        : raw.preferred_language != null
          ? String(raw.preferred_language)
          : null,
    preferredTimezone:
      raw.preferredTimezone != null && String(raw.preferredTimezone).trim()
        ? String(raw.preferredTimezone).trim()
        : raw.preferred_timezone != null && String(raw.preferred_timezone).trim()
          ? String(raw.preferred_timezone).trim()
          : null,
    organizations,
    pendingOrganizations,
    pendingOrganizationInvites,
    hasOrganizationMembership: Boolean(
      raw.hasOrganizationMembership ?? organizations.length > 0,
    ),
    hasPendingOrganizationMembership: Boolean(
      raw.hasPendingOrganizationMembership ?? pendingOrganizations.length > 0,
    ),
    hasProductAccess: Boolean(raw.hasProductAccess),
    roles: roleNames,
    globalRoles,
    permissions: permissionNames,
    navigation,
    createdAt: String(raw.createdAt ?? ""),
    updatedAt: String(raw.updatedAt ?? ""),
    ...parseDisabledBySnapshot(raw),
    smartLinkSyncStatus:
      (raw.smartLinkSyncStatus as User["smartLinkSyncStatus"]) ?? null,
  };

  syncNavigationCache(navigation);
  return user;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  avatarUrl?: string | null;
  phone?: string | null;
  countryCode?: string | null;
  clearPhone?: boolean;
  preferredLanguage?: string | null;
  preferredTimezone?: string | null;
}

export interface UpdateLocaleRequest {
  preferredLanguage?: string | null;
  preferredTimezone?: string | null;
}

/**
 * Update another user's language / timezone.
 * Allowed for user:update holders, or org admins who share an organization with the target.
 */
export async function updateUserLocale(
  accessToken: string,
  userId: string,
  data: UpdateLocaleRequest,
): Promise<User> {
  const raw = await authenticatedProApiFetch<Record<string, unknown>>(
    `/users/${userId}/locale`,
    accessToken,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    },
  );
  return normalizeUserProfile(raw);
}

export interface UpdatePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

/**
 * Get current user profile
 * Uses pro-api which proxies to user-service
 */
export async function getCurrentUser(accessToken: string): Promise<User> {
  // Retry-on-401 refreshes the access token once instead of failing into a
  // heavier refresh + re-bootstrap waterfall during session hydrate.
  const raw = await authenticatedProApiFetchWithRetry<Record<string, unknown>>(
    '/users/me',
    accessToken,
    { method: 'GET' },
  );
  return normalizeUserProfile(raw);
}

/**
 * Mark initial profile setup wizard as complete.
 * No verification steps required — only sets profile_setup_completed_at on the user record.
 * Uses user-service directly (same pattern as password update).
 */
export async function completeProfileSetup(accessToken: string): Promise<User> {
  const { authenticatedFetch } = await import('./client');
  const raw = await authenticatedFetch<Record<string, unknown>>(
    '/users/me/complete-profile-setup',
    accessToken,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return normalizeUserProfile(raw);
}

/**
 * Update current user profile
 * Uses pro-api which proxies to user-service
 */
export async function updateProfile(
  accessToken: string,
  data: UpdateProfileRequest,
): Promise<User> {
  const raw = await authenticatedProApiFetch<Record<string, unknown>>('/users/me', accessToken, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  return normalizeUserProfile(raw);
}

/**
 * Remove phone number and reset phone verification on the current user.
 */
export async function removePhone(accessToken: string): Promise<User> {
  return updateProfile(accessToken, { clearPhone: true });
}

/**
 * Update current user password
 * Note: This still uses user-service directly since pro-api doesn't proxy it yet
 * TODO: Add password update endpoint to pro-api
 */
export async function updatePassword(
  accessToken: string,
  data: UpdatePasswordRequest,
): Promise<{ message: string }> {
  // For now, still use user-service directly
  // In the future, this should go through pro-api
  const { authenticatedFetch } = await import('./client');
  return authenticatedFetch<{ message: string }>('/users/me/password', accessToken, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Get user's effective permissions (current user)
 * Uses pro-api which proxies to user-service
 */
export async function getMyPermissions(
  accessToken: string,
): Promise<{ permissions: string[] }> {
  return authenticatedProApiFetch<{ permissions: string[] }>(
    '/users/me/permissions',
    accessToken,
    {
      method: 'GET',
    },
  );
}

/**
 * Get user by ID (admin only)
 * Note: This endpoint may need to be added to pro-api
 * For now, keeping as a placeholder
 */
export async function getUserById(
  accessToken: string,
  userId: string,
): Promise<User> {
  // TODO: Add this endpoint to pro-api
  const { authenticatedFetch } = await import('./client');
  return authenticatedFetch<User>(`/users/${userId}`, accessToken, {
    method: 'GET',
  });
}

/**
 * Get user's effective permissions by user ID
 * Note: This endpoint may need to be added to pro-api
 */
export interface CreateOrganizationRequest {
  name: string;
  slug: string;
  description?: string;
  logoUrl?: string | null;
  type?: string;
  email?: string;
  phone?: string;
  address?: string;
  addressSecondary?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  timezone?: string;
  emailPatterns?: string[];
  isMocked?: boolean;
}

export async function checkOrganizationName(
  accessToken: string,
  name: string,
): Promise<{ available: boolean }> {
  return authenticatedProApiFetch<{ available: boolean }>(
    `/organizations/check-name?name=${encodeURIComponent(name)}`,
    accessToken,
    { method: 'GET' },
  );
}

export async function createOrganization(
  accessToken: string,
  data: CreateOrganizationRequest,
): Promise<{ id: string } & Record<string, unknown>> {
  return authenticatedProApiFetch<{ id: string } & Record<string, unknown>>(
    '/organizations',
    accessToken,
    { method: 'POST', body: JSON.stringify(data) },
  );
}

export interface Organization {
  id: string;
  isMocked?: boolean;
  name: string;
  slug: string;
  description?: string | null;
  logoUrl?: string | null;
  type?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  addressSecondary?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  timezone?: string | null;
  createdAt?: string;
  updatedAt?: string;
  emailPatterns?: string[];
  roleName?: string | null;
  roleColor?: string | null;
  deletedAt?: string | null;
  disabledByUserId?: string | null;
  disabledByName?: string | null;
  disabledByEmail?: string | null;
}

export type UpdateOrganizationRequest = Partial<CreateOrganizationRequest>;

export interface OrganizationWithMembership extends Organization {
  roleName?: string | null;
  roleColor?: string | null;
  isPrimary?: boolean;
  isMember: boolean;
  isPendingRequest?: boolean;
  isPreApproved?: boolean;
}

export async function fetchOrganizations(
  accessToken: string,
  options?: { includeDeleted?: boolean },
): Promise<Organization[]> {
  const query = options?.includeDeleted ? "?includeDeleted=1" : "";
  const data = await authenticatedProApiFetchWithRetry<Organization[]>(
    `/organizations${query}`,
    accessToken,
    { method: 'GET' },
  );
  if (!Array.isArray(data)) return [];
  return data.map((row) => parseOrganizationRecord(row as unknown as Record<string, unknown>));
}

function parseOrganizationRecord(raw: Record<string, unknown>): Organization {
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    slug: String(raw.slug ?? ''),
    description: raw.description != null ? String(raw.description) : null,
    logoUrl: parseOrganizationLogoUrl(raw),
    type: raw.type != null ? String(raw.type) : null,
    email: raw.email != null ? String(raw.email) : null,
    phone: raw.phone != null ? String(raw.phone) : null,
    address: raw.address != null ? String(raw.address) : null,
    addressSecondary:
      raw.addressSecondary != null ? String(raw.addressSecondary) : null,
    city: raw.city != null ? String(raw.city) : null,
    state: raw.state != null ? String(raw.state) : null,
    zip: raw.zip != null ? String(raw.zip) : null,
    country: raw.country != null ? String(raw.country) : null,
    timezone: raw.timezone != null ? String(raw.timezone) : null,
    createdAt: raw.createdAt != null ? String(raw.createdAt) : undefined,
    updatedAt: raw.updatedAt != null ? String(raw.updatedAt) : undefined,
    emailPatterns: Array.isArray(raw.emailPatterns)
      ? (raw.emailPatterns as string[])
      : [],
    roleName: raw.roleName != null ? String(raw.roleName) : null,
    roleColor: raw.roleColor != null ? String(raw.roleColor) : null,
    isMocked: Boolean(raw.isMocked ?? raw.is_mocked ?? false),
    ...parseDisabledBySnapshot(raw),
  };
}

function parseOrganizationLogoUrl(raw: Record<string, unknown>): string | null {
  if (raw.logoUrl != null && String(raw.logoUrl).trim()) {
    return String(raw.logoUrl);
  }
  if (raw.logo_url != null && String(raw.logo_url).trim()) {
    return String(raw.logo_url);
  }
  return null;
}

export async function fetchJoinableOrganizations(
  accessToken: string,
): Promise<OrganizationWithMembership[]> {
  const data = await authenticatedProApiFetchWithRetry<{
    organizations: OrganizationWithMembership[];
  }>('/organizations/joinable', accessToken, { method: 'GET' });
  if (!Array.isArray(data.organizations)) return [];
  return data.organizations.map((row) => {
    const raw = row as unknown as Record<string, unknown>;
    return {
      ...row,
      logoUrl: parseOrganizationLogoUrl(raw),
      isMember: Boolean(raw.isMember ?? raw.is_member),
      isPendingRequest: Boolean(raw.isPendingRequest ?? raw.is_pending_request),
      isPreApproved: Boolean(raw.isPreApproved ?? raw.is_pre_approved),
    };
  });
}

export async function searchOrganizations(
  accessToken: string,
  query: string,
  limit = 20,
): Promise<OrganizationWithMembership[]> {
  const data = await authenticatedProApiFetchWithRetry<{
    organizations: OrganizationWithMembership[];
  }>(
    `/organizations/search?q=${encodeURIComponent(query.trim())}&limit=${limit}`,
    accessToken,
    { method: 'GET' },
  );
  if (!Array.isArray(data.organizations)) return [];
  return data.organizations.map((row) => {
    const raw = row as unknown as Record<string, unknown>;
    return {
      ...row,
      logoUrl: parseOrganizationLogoUrl(raw),
      isMember: Boolean(raw.isMember ?? raw.is_member),
      isPendingRequest: Boolean(raw.isPendingRequest ?? raw.is_pending_request),
      isPreApproved: Boolean(raw.isPreApproved ?? raw.is_pre_approved),
    };
  });
}

export async function requestOrganizationMembership(
  accessToken: string,
  organizationId: string,
): Promise<void> {
  await authenticatedProApiFetchWithRetry(
    `/organizations/${organizationId}/membership-requests`,
    accessToken,
    { method: 'POST' },
  );
}

export async function cancelOrganizationMembershipRequest(
  accessToken: string,
  organizationId: string,
): Promise<void> {
  await authenticatedProApiFetchWithRetry(
    `/organizations/${organizationId}/membership-requests`,
    accessToken,
    { method: 'DELETE' },
  );
}

export async function instantJoinOrganization(
  accessToken: string,
  organizationId: string,
): Promise<void> {
  await authenticatedProApiFetchWithRetry(
    `/organizations/${organizationId}/instant-join`,
    accessToken,
    { method: 'POST' },
  );
}

export type OrganizationMemberStatus = 'active' | 'pending';

export interface OrganizationMember {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl?: string | null;
  roleId?: string | null;
  roleName?: string | null;
  roleLabel?: string | null;
  roleColor?: string | null;
  roleSystemKey?: string | null;
  membershipStatus: OrganizationMemberStatus;
  /** Original invite email when it differs from the account email */
  invitedAs?: string | null;
  /** Present when the user account is disabled (soft-deleted). */
  deletedAt?: string | null;
  /** ISO timestamp when the user joined the organization. */
  createdAt?: string;
}

export interface OrganizationPendingInvite {
  inviteId: string;
  email: string;
  roleId: string;
  roleName?: string | null;
  roleColor?: string | null;
  expiresAt: string;
  createdAt: string;
}

export interface OrganizationScopedRole {
  id: string;
  name: string;
  label?: string | null;
  description?: string | null;
  color?: string | null;
  scope: string;
  systemKey?: "admin" | "member" | string | null;
  permissions?: string[];
}

export interface OrgAssignablePermission {
  id: string;
  name: string;
  description?: string | null;
  resource?: string | null;
  action?: string | null;
}

export async function fetchOrganizationMembers(
  accessToken: string,
  orgId: string,
): Promise<OrganizationMember[]> {
  const data = await authenticatedProApiFetchWithRetry<{ members: OrganizationMember[] }>(
    `/organizations/${orgId}/members`,
    accessToken,
    { method: 'GET' },
  );
  if (!Array.isArray(data.members)) return [];
  return data.members.map((row) => {
    const raw = row as unknown as Record<string, unknown>;
    return {
      userId: String(raw.userId ?? raw.user_id ?? ''),
      firstName: String(raw.firstName ?? raw.first_name ?? ''),
      lastName: String(raw.lastName ?? raw.last_name ?? ''),
      email: String(raw.email ?? ''),
      avatarUrl:
        raw.avatarUrl != null
          ? String(raw.avatarUrl)
          : raw.avatar_url != null
            ? String(raw.avatar_url)
            : null,
      roleId:
        raw.roleId != null
          ? String(raw.roleId)
          : raw.role_id != null
            ? String(raw.role_id)
            : null,
      roleName:
        raw.roleName != null
          ? String(raw.roleName)
          : raw.role_name != null
            ? String(raw.role_name)
            : null,
      roleLabel:
        raw.roleLabel != null
          ? String(raw.roleLabel)
          : raw.role_label != null
            ? String(raw.role_label)
            : null,
      roleColor:
        raw.roleColor != null
          ? String(raw.roleColor)
          : raw.role_color != null
            ? String(raw.role_color)
            : null,
      roleSystemKey:
        raw.roleSystemKey != null
          ? String(raw.roleSystemKey)
          : raw.role_system_key != null
            ? String(raw.role_system_key)
            : null,
      membershipStatus:
        raw.membershipStatus === 'pending' || raw.membership_status === 'pending'
          ? 'pending'
          : 'active',
      invitedAs:
        raw.invitedAs != null
          ? String(raw.invitedAs)
          : raw.invited_as != null
            ? String(raw.invited_as)
            : null,
      createdAt: raw.createdAt != null
        ? String(raw.createdAt)
        : raw.created_at != null
          ? String(raw.created_at)
          : undefined,
    };
  });
}

export async function fetchOrganizationPendingInvites(
  accessToken: string,
  orgId: string,
): Promise<OrganizationPendingInvite[]> {
  const data = await authenticatedProApiFetchWithRetry<{
    invites: OrganizationPendingInvite[];
  }>(`/organizations/${orgId}/invites`, accessToken, { method: 'GET' });
  if (!Array.isArray(data.invites)) return [];
  return data.invites.map((row) => {
    const raw = row as unknown as Record<string, unknown>;
    return {
      inviteId: String(raw.inviteId ?? raw.invite_id ?? ''),
      email: String(raw.email ?? ''),
      roleId: String(raw.roleId ?? raw.role_id ?? ''),
      roleName:
        raw.roleName != null
          ? String(raw.roleName)
          : raw.role_name != null
            ? String(raw.role_name)
            : null,
      roleColor:
        raw.roleColor != null
          ? String(raw.roleColor)
          : raw.role_color != null
            ? String(raw.role_color)
            : null,
      expiresAt: String(raw.expiresAt ?? raw.expires_at ?? ''),
      createdAt: String(raw.createdAt ?? raw.created_at ?? ''),
    };
  });
}

export async function cancelOrganizationInvite(
  accessToken: string,
  orgId: string,
  inviteId: string,
): Promise<{ message: string }> {
  return authenticatedProApiFetchWithRetry<{ message: string }>(
    `/organizations/${orgId}/invites/${inviteId}`,
    accessToken,
    { method: 'DELETE' },
  );
}

export interface InvitePreview {
  valid: boolean;
  expired?: boolean;
  email?: string;
  organizationId?: string;
  organizationName?: string;
  intent?: "membership" | "certification";
}

export async function fetchInvitePreview(token: string): Promise<InvitePreview> {
  return proApiFetch<InvitePreview>(
    `/invites/preview?token=${encodeURIComponent(token.trim())}`,
    { method: 'GET' },
  );
}

/** Accept a pending org invite (creates membership + marks invite accepted). */
export async function claimOrganizationInvite(
  accessToken: string,
  token: string,
): Promise<{
  organizationId: string;
  organizationName?: string;
  intent?: "membership" | "certification";
  alreadyMember?: boolean;
  alreadyClaimed?: boolean;
}> {
  return authenticatedProApiFetchWithRetry(
    '/invites/claim',
    accessToken,
    {
      method: 'POST',
      body: JSON.stringify({ token: token.trim() }),
    },
  );
}

export async function createOrganizationInvite(
  accessToken: string,
  orgId: string,
  body: {
    email: string;
    roleId: string;
    intent?: "membership" | "certification";
  },
): Promise<void> {
  await authenticatedProApiFetchWithRetry(
    `/organizations/${orgId}/invites`,
    accessToken,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export async function addOrganizationMember(
  accessToken: string,
  orgId: string,
  body: { userId: string; roleId?: string | null },
): Promise<void> {
  await authenticatedProApiFetchWithRetry(
    `/organizations/${orgId}/members`,
    accessToken,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export async function fetchOrganizationRoles(
  accessToken: string,
  orgId: string,
): Promise<OrganizationScopedRole[]> {
  const data = await authenticatedProApiFetchWithRetry<{ roles: OrganizationScopedRole[] }>(
    `/organizations/${orgId}/roles`,
    accessToken,
    { method: 'GET' },
  );
  if (!Array.isArray(data.roles)) return [];
  return data.roles.map((row) => {
    const raw = row as unknown as Record<string, unknown>;
    const permissionsRaw = raw.permissions;
    return {
      id: String(raw.id ?? ""),
      name: String(raw.name ?? ""),
      label: raw.label != null ? String(raw.label) : null,
      description:
        raw.description != null ? String(raw.description) : null,
      color: raw.color != null ? String(raw.color) : null,
      scope: String(raw.scope ?? "organization"),
      systemKey:
        raw.systemKey != null
          ? String(raw.systemKey)
          : raw.system_key != null
            ? String(raw.system_key)
            : null,
      permissions: Array.isArray(permissionsRaw)
        ? permissionsRaw.map(String)
        : [],
    };
  });
}

export async function fetchOrganizationAvailablePermissions(
  accessToken: string,
  orgId: string,
): Promise<OrgAssignablePermission[]> {
  const data = await authenticatedProApiFetchWithRetry<{
    permissions: OrgAssignablePermission[];
  }>(`/organizations/${orgId}/available-permissions`, accessToken, {
    method: "GET",
  });
  if (!Array.isArray(data.permissions)) return [];
  return data.permissions.map((row) => {
    const raw = row as unknown as Record<string, unknown>;
    return {
      id: String(raw.id ?? ""),
      name: String(raw.name ?? ""),
      description: raw.description != null ? String(raw.description) : null,
      resource: raw.resource != null ? String(raw.resource) : null,
      action: raw.action != null ? String(raw.action) : null,
    };
  });
}

export async function createOrganizationRole(
  accessToken: string,
  orgId: string,
  body: {
    label: string;
    color?: string | null;
    description?: string | null;
    permissionNames?: string[];
  },
): Promise<OrganizationScopedRole> {
  const data = await authenticatedProApiFetchWithRetry<{
    role: OrganizationScopedRole;
  }>(`/organizations/${orgId}/roles`, accessToken, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data.role;
}

export async function updateOrganizationRole(
  accessToken: string,
  orgId: string,
  roleId: string,
  body: {
    label?: string;
    color?: string | null;
    description?: string | null;
    permissionNames?: string[];
  },
): Promise<OrganizationScopedRole> {
  const data = await authenticatedProApiFetchWithRetry<{
    role: OrganizationScopedRole;
  }>(`/organizations/${orgId}/roles/${roleId}`, accessToken, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  return data.role;
}

export async function deleteOrganizationRole(
  accessToken: string,
  orgId: string,
  roleId: string,
): Promise<void> {
  await authenticatedProApiFetchWithRetry(
    `/organizations/${orgId}/roles/${roleId}`,
    accessToken,
    { method: "DELETE" },
  );
}

export async function approveOrganizationMembershipRequest(
  accessToken: string,
  orgId: string,
  userId: string,
): Promise<void> {
  await authenticatedProApiFetchWithRetry(
    `/organizations/${orgId}/membership-requests/${userId}/approve`,
    accessToken,
    { method: 'POST', body: JSON.stringify({}) },
  );
}

export async function denyOrganizationMembershipRequest(
  accessToken: string,
  orgId: string,
  userId: string,
): Promise<void> {
  await authenticatedProApiFetchWithRetry(
    `/organizations/${orgId}/membership-requests/${userId}/deny`,
    accessToken,
    { method: 'POST' },
  );
}

export async function leaveOrganization(
  accessToken: string,
  organizationId: string,
  userId: string,
): Promise<void> {
  await authenticatedProApiFetchWithRetry(
    `/organizations/${organizationId}/members/${userId}`,
    accessToken,
    { method: 'DELETE' },
  );
}

export async function removeOrganizationMember(
  accessToken: string,
  organizationId: string,
  userId: string,
): Promise<void> {
  await authenticatedProApiFetchWithRetry(
    `/organizations/${organizationId}/members/${userId}`,
    accessToken,
    { method: 'DELETE' },
  );
}

export async function updateOrganizationMemberRole(
  accessToken: string,
  orgId: string,
  userId: string,
  roleId: string | null,
): Promise<void> {
  await authenticatedProApiFetchWithRetry(
    `/organizations/${orgId}/members/${userId}/role`,
    accessToken,
    {
      method: 'PATCH',
      body: JSON.stringify({ roleId }),
    },
  );
}

export async function disableOrganization(
  accessToken: string,
  orgId: string,
): Promise<{ message: string }> {
  return authenticatedProApiFetchWithRetry<{ message: string }>(
    `/organizations/${orgId}/soft-delete`,
    accessToken,
    { method: 'POST' },
  );
}

export async function restoreOrganization(
  accessToken: string,
  orgId: string,
): Promise<{ message: string }> {
  return authenticatedProApiFetchWithRetry<{ message: string }>(
    `/organizations/${orgId}/undelete`,
    accessToken,
    { method: 'POST' },
  );
}

export async function fetchMockedOrgCount(accessToken: string): Promise<number> {
  const res = await authenticatedProApiFetchWithRetry<{ count: number }>('/organizations/mocked/count', accessToken, { method: 'GET' });
  return res.count;
}

export async function clearMockedOrgs(accessToken: string): Promise<number> {
  const res = await authenticatedProApiFetchWithRetry<{ deleted: number }>('/organizations/mocked', accessToken, { method: 'DELETE' });
  return res.deleted;
}

export async function fetchMockedUserCount(accessToken: string): Promise<number> {
  const res = await authenticatedProApiFetchWithRetry<{ count: number }>('/users/mocked/count', accessToken, { method: 'GET' });
  return res.count;
}

export async function clearMockedUsers(accessToken: string): Promise<number> {
  const res = await authenticatedProApiFetchWithRetry<{ deleted: number }>('/users/mocked', accessToken, { method: 'DELETE' });
  return res.deleted;
}

export async function deleteOrganizationPermanently(
  accessToken: string,
  orgId: string,
): Promise<{ message: string }> {
  return authenticatedProApiFetchWithRetry<{ message: string }>(
    `/organizations/${orgId}/hard-delete`,
    accessToken,
    { method: 'POST' },
  );
}

export async function fetchOrganization(
  accessToken: string,
  orgId: string,
  options?: { includeDeleted?: boolean },
): Promise<Organization> {
  const query = options?.includeDeleted ? "?includeDeleted=1" : "";
  const raw = await authenticatedProApiFetchWithRetry<Record<string, unknown>>(
    `/organizations/${orgId}${query}`,
    accessToken,
    { method: 'GET' },
  );
  return parseOrganizationRecord(raw);
}

export async function updateOrganization(
  accessToken: string,
  orgId: string,
  data: UpdateOrganizationRequest,
): Promise<Organization> {
  const raw = await authenticatedProApiFetchWithRetry<Record<string, unknown>>(
    `/organizations/${orgId}`,
    accessToken,
    { method: 'PATCH', body: JSON.stringify(data) },
  );
  return fetchOrganization(accessToken, String(raw.id ?? orgId));
}

export async function getUserPermissions(
  accessToken: string,
  userId: string,
): Promise<{ permissions: string[] }> {
  // TODO: Add this endpoint to pro-api
  const { authenticatedFetch } = await import('./client');
  return authenticatedFetch<{ permissions: string[] }>(
    `/users/${userId}/permissions`,
    accessToken,
    {
      method: 'GET',
    },
  );
}

